import type { Hub, Scope } from '@sentry/browser';

import { getActiveTrace } from '@atlaskit/react-ufo/get-active-trace';
import { addErrorToAll } from '@atlaskit/react-ufo/interaction-metrics';

import { getLogger } from '@confluence/logger';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getBuildInfo } from '@confluence/build-info';
import { isResolved, getValue } from '@confluence/lifted-promise';
import { containsNetworkError } from '@confluence/network';
import { fg } from '@confluence/feature-gating';

import { getSentryHubForSLAErrors } from './sentry.ts';
import { Batcher } from './Batcher.ts';
import { isModuleNotFoundError } from './isModuleNotFoundError.ts';
import type { MonitoringClient, ErrorData, MonitoringContext } from './MonitoringClient.ts';
import { getLogSafeErrorAttributes, type LogSafeErrorAttributes } from './error-attributes.ts';

type MetricEvent = {
	type: 'timing' | 'gauge' | 'increment' | 'decrement';
	name: string;
	tags: string[];
	value: number;
	/**
	 * ISO string
	 *
	 * @type {string}
	 */
	timestamp: string;
	/**
	 *
	 * @type {number}
	 */
	sampleRate: number;
};
type ErrorEvent = {
	event: ErrorAttributes;
	tags?: string[];
};
type ErrorAttributes = { error: string; [key: string]: any };

type TelemetryPayload = {
	type: 'metric' | 'log' | 'trace' | 'analytics';
	data: MetricEvent[];
	meta: {
		globalTags: string[];
	};
};
type TelemetryErrorPayload = {
	type: 'error';
	data: ErrorEvent[];
	meta: {
		globalTags: string[];
	};
};

const logger = getLogger('CustomMonitoringClient');

const getIsBifrostTag = () => `isBifrost:${window?.__IS_BIFROST__ ?? false}`;

export type CustomMonitoringClientOptions = {
	/**
	 * Maximum number of errors to submit per transition.
	 *
	 * This is mainly to counter infinite error submission loops, where a single user may be submitting thousands (or even millions) of errors due to some code being stuck in an infinite retry loop.
	 */
	errorsPerTransitionLimit?: number;

	/**
	 * Specifies the maximum number of metrics to be sent out at once. If there are more metrics then this number, they will be split into separate batches.
	 */
	metricBatchMaxSize?: number;

	/**
	 * Specifies how often will the metrics queue be flushed.
	 *
	 * Generally, the metrics are sent out once the `metricBatchMaxSize` has been reached. This interval value will allow for the sending of non-full metric batches.
	 */
	metricBatchFlushIntervalMs?: number;

	/**
	 * Specifies the maximum number of errors to be sent to the telemetry service at once. If there are more errors then this number, they will be split into separate batches.
	 */
	errorBatchMaxSize?: number;

	/**
	 * Specifies how often will the errors queue be flushed.
	 *
	 * Generally, the errors are sent out once the `errorBatchMaxSize` has been reached. This interval value will allow for the sending of non-full error batches.
	 */
	errorBatchFlushIntervalMs?: number;

	/**
	 * Specifies what each error event's 1) error attribute, 2) stack attribute, and 3) componentStack attribute will be trimmed to before being batched & sent to telemetry service.
	 *
	 * It's recommended to trim the error events prior to sending the batch to telemetry service, as there is a 64KB request size limit. If set to 0, errors won't have their attributes trimmed. Trimmed strings end in "..."
	 */
	errorBatchMsgTrimLength?: number;
};

export class CustomMonitoringClient implements MonitoringClient {
	private batcher: Batcher<MetricEvent>;
	private errorBatcher: Batcher<ErrorEvent>;
	private isWindowUnloading = false;
	private context: MonitoringContext = {};
	// First error occurred in initial load or transition
	private firstErrorInSession = true;

	private newSentryEnabled = false;
	private sendErrorsToTelemetryService = false;

	private errorsPerTransitionLimit: number;
	private errorsPerCurrentTransition: number = 0;
	private errorBatchMsgTrimLength: number;

	constructor({
		errorsPerTransitionLimit = 1000,
		metricBatchMaxSize = 50,
		metricBatchFlushIntervalMs = 10000,
		errorBatchFlushIntervalMs = 10000,
		errorBatchMaxSize = 10,
		errorBatchMsgTrimLength = 1000,
	}: CustomMonitoringClientOptions = {}) {
		this.errorsPerTransitionLimit = errorsPerTransitionLimit;
		this.errorBatchMsgTrimLength = errorBatchMsgTrimLength;

		this.batcher = new Batcher(metricBatchFlushIntervalMs, metricBatchMaxSize);
		this.errorBatcher = new Batcher(errorBatchFlushIntervalMs, errorBatchMaxSize);

		this.batcher.onFlush((batch) => {
			this.onBatch(batch);
		});
		this.errorBatcher.onFlush((batch) => {
			this.onErrorBatch(batch);
		});

		const unloadListener = () => {
			this.isWindowUnloading = true;
			this.batcher.forceFlush();
			this.errorBatcher.forceFlush();
		};
		window.addEventListener('pagehide', unloadListener);
	}

	public enableNewSentry(): void {
		this.newSentryEnabled = true;
	}

	public enableSendErrorsToTelemetryService(): void {
		this.sendErrorsToTelemetryService = true;
	}

	private shouldIgnoreError(error: any): boolean {
		return isModuleNotFoundError(error);
	}

	public submitError(error: any, errorData: ErrorData): void {
		if (!error) {
			return;
		}

		if (this.shouldIgnoreError(error)) {
			return;
		}

		if (this.errorsPerCurrentTransition === this.errorsPerTransitionLimit) {
			// limit reached; do not submit the errors
			return;
		}
		this.errorsPerCurrentTransition++;

		const logSafeErrorAttributes = getLogSafeErrorAttributes(error);
		const { attribution } = errorData;
		let isFirstInSession = this.firstErrorInSession;

		const errorWithStackTrace: Error =
			error && error.stack && error.message
				? error
				: new Error((error && error.message) || String(error));

		if (isErrorIgnorableByOnCall(errorWithStackTrace, attribution, logSafeErrorAttributes)) {
			isFirstInSession = false;
		} else {
			this.firstErrorInSession = false;
		}

		const errorCounterTags = [
			`attribution:${attribution}`,
			`firstInSession:${isFirstInSession}`,
			...(logSafeErrorAttributes.operationName
				? [`queryName:${logSafeErrorAttributes.operationName}`]
				: []),
		];

		if (this.context.shard) {
			errorCounterTags.push(`shard:${this.context.shard}`);
		}

		errorCounterTags.push(getIsBifrostTag());
		errorCounterTags.push(`isMultiThread:${window?.__IS_MULTITHREADED_RUNTIME__}`);

		this.incrementCounter('jsErrors', errorCounterTags);

		const sentryTags: { [key: string]: string } = {
			attribution,
		};

		if (this.context.slaExperience) {
			sentryTags.slaExperience = this.context.slaExperience;
		}
		if (this.context.pageName) {
			sentryTags.pageName = this.context.pageName;
		}
		if (logSafeErrorAttributes.failedSlaExperience) {
			sentryTags.failedSlaExperience = logSafeErrorAttributes.failedSlaExperience;
		}
		if (logSafeErrorAttributes.operationName) {
			sentryTags.operationName = logSafeErrorAttributes.operationName;
		}

		sentryTags.ssrRendered = Boolean((window as any).__SSR_RENDERED__).toString();

		if (logSafeErrorAttributes.failedSlaExperience) {
			this.logSlaErrorToSentry(errorWithStackTrace, sentryTags).catch((sentryError) => {
				void this.logError(sentryError, { attribution: 'backbone' });
			});
		} else if (this.newSentryEnabled) {
			this.logErrorToSentry(errorWithStackTrace, sentryTags);
		}

		void this.logError(errorWithStackTrace, {
			...errorData,
			...logSafeErrorAttributes,
			firstInSession: isFirstInSession,
		});
	}

	public updateContext(update: Partial<MonitoringContext>): void {
		if (
			update.transitionId !== undefined &&
			(update.transitionId === 0 || this.context.transitionId !== update.transitionId)
		) {
			const tags = [`type:${update.transitionId === 0 ? 'initial' : 'transition'}`];

			tags.push(getIsBifrostTag());
			tags.push(`isMultiThread:${window?.__IS_MULTITHREADED_RUNTIME__}`);

			this.incrementCounter('session', tags);

			// Next error will be first in the session when there is a initial load (transition id = 0) or a SPA transition.
			this.firstErrorInSession = true;
			this.errorsPerCurrentTransition = 0;
		}

		if (update.pageName && this.context.pageName !== update.pageName) {
			// since the page name changed, all the events in the current batch were done
			// under an old page name. Hence we force-flush the batch, and only then set the new context.
			if (this.context.pageName !== undefined) {
				// skip force-flush if that's the first time we set pageName (i.e. initial navigation)
				this.batcher.forceFlush();
			}
		}
		Object.assign(this.context, update);
	}

	public incrementCounter(counterName: string, tags?: string[]): void {
		this.batcher.add(
			this.createMetric({
				type: 'increment',
				name: counterName,
				value: 1,
				tags,
			}),
		);
	}

	public gauge(counterName: string, value: number, tags?: string[]): void {
		this.batcher.add(
			this.createMetric({
				type: 'gauge',
				name: counterName,
				value,
				tags,
			}),
		);
	}

	private logErrorToSentry(error: Error, tags: { [id: string]: string }) {
		import(/* webpackChunkName: "loadable-sentrybrowser" */ '@sentry/browser')
			.then(({ withScope, captureException }) => {
				withScope((scope: Scope) => {
					scope.setTags(tags);
					captureException(error);
				});
			})
			.catch((sentryError) => {
				void this.logError(sentryError, { attribution: 'backbone' });
			});
	}

	private async logSlaErrorToSentry(error: Error, tags: { [id: string]: string }) {
		let sentryHub: Hub | null | undefined = null;

		const standaloneSentryPromise = getSentryHubForSLAErrors();
		if (isResolved(standaloneSentryPromise)) {
			sentryHub = getValue(standaloneSentryPromise);
		} else {
			sentryHub = await standaloneSentryPromise;
		}

		if (sentryHub) {
			sentryHub.withScope((scope: Scope) => {
				scope.setTags(tags);
				sentryHub!.captureException(error);
			});
		}
	}

	/**
	 * There is a 64kb payload limit for navigator.sendBeacon requests
	 * 10 events sent max.
	 * 3 fields that are being trimmed
	 * That allows for 65536/10/3 = 2184.5 per field
	 * we add a bit of buffer for the rest of the payload by reducing that number further to 1000
	 * @param errorAttributes
	 * @returns a new object with trimmed attributes if they exceed the limit determined by
	 * errorBatchMsgTrimLength` which is calculated based on the following details
	 */
	private trimAttributes(errorAttributes: ErrorAttributes) {
		const attributesToTrim = ['error', 'stack', 'componentStack'];
		const newErrorAttributes = { ...errorAttributes };

		if (
			errorAttributes.error?.startsWith?.('Minified React error #130') &&
			fg('confluence_frontend_untrimmed_react_errors')
		) {
			// Oct 2025: Log the (hopefully entire) stack for minified react error 130, to aid investigations into increased occurrences. Still trims to <64kb limit.
			newErrorAttributes.error = this.trimTextLength(newErrorAttributes.error, 2000);
			newErrorAttributes.stack = this.trimTextLength(newErrorAttributes.stack, 25000);
			newErrorAttributes.componentStack = this.trimTextLength(
				newErrorAttributes.componentStack,
				25000,
			);
		} else {
			attributesToTrim.forEach((attribute) => {
				if (newErrorAttributes[attribute]) {
					newErrorAttributes[attribute] = this.trimTextLength(
						newErrorAttributes[attribute],
						this.errorBatchMsgTrimLength,
					);
				}
			});
		}
		return newErrorAttributes;
	}

	private async logError(
		error: Error,
		additionalData: Record<string, string | number | boolean | null | undefined>,
	): Promise<void> {
		try {
			const analyticsWebClientPromise = getAnalyticsWebClient();
			let client;
			if (isResolved(analyticsWebClientPromise)) {
				client = getValue(analyticsWebClientPromise);
			} else {
				client = await analyticsWebClientPromise;
			}

			const errorAttributes: ErrorAttributes = {
				error: error.message || String(error),
				stack: error.stack,
				browserInfo: window.navigator.userAgent,
				ssrRendered: Boolean((window as any).__SSR_RENDERED__),
				releaseId: getBuildInfo().FRONTEND_VERSION,
				track: getBuildInfo().RELEASE_TRACK,
				pageName: this.context.pageName,
				shard: this.context.shard,
				edition: this.context.edition,
				jqVer: (window as any).jQuery?.fn?.jquery,
				activeExperiences: this.context.activeExperiences
					? Array.from(this.context.activeExperiences)
					: undefined,
				slaExperience: this.context.slaExperience,
				ufoTraceId: getActiveTrace()?.traceId,
				ufoTraceType: getActiveTrace()?.type,
				...additionalData,
			};

			const trimmedErrorAttributes = this.trimAttributes(errorAttributes);

			const hydrationFailed =
				window && window.__HYDRATION_FAILED__ && fg('confluence_prevent_hydration_ufo_abort');

			if (!hydrationFailed) {
				addErrorToAll(
					'confluence-frontend-monitoring-client',
					null,
					'interaction unhandled error',
					errorAttributes.error,
				);
			}

			client.sendOperationalEvent({
				source: 'ui',
				action: 'unhandledError',
				actionSubject: 'ui',
				attributes: trimmedErrorAttributes,
			});

			// Push unhandled errors to a window array so synthetic test runners can read them.
			// We check window.__IS_SYNTHETIC_ENV__ (set in SPA entry point) instead of importing
			// StatsigConfigurations directly, because it would create unavoidable circular dependency.
			if (window && window.__IS_SYNTHETIC_ENV__) {
				if (!window.__UNHANDLED_ERRORS__) {
					window.__UNHANDLED_ERRORS__ = [];
				}

				window.__UNHANDLED_ERRORS__.push({
					source: 'ui',
					action: 'unhandledError',
					actionSubject: 'ui',
					attributes: trimmedErrorAttributes,
				});
			}

			if (this.sendErrorsToTelemetryService) {
				this.errorBatcher.add({
					event: trimmedErrorAttributes,
				});
			}
		} catch {
			// no-op, if an error occurs here, there's really nowhere to submit it
		}
	}

	/**
	 * Defines an allow-list of context attributes that will be sent as "global" tags to FE Telemetry and SignalFX with every event.
	 * Tags additionally have to be allow-listed in the FE Telemetry repository. We PURPOSELY exclude certain tags (that are allowed by FE Telemetry)
	 * such as "edition", as we do not want to be sending that tag for every experience event (thereby increasing metric cardinality). Instead, those tags
	 * are included with specific experience events.
	 * @private
	 */
	private formatContextAsTags(): string[] {
		const tags: string[] = [];

		if (this.context.pageName) {
			tags.push(`page:${this.context.pageName}`);
		}

		if (this.context.tenantId) {
			tags.push(`cloudId:${this.context.tenantId}`);
		}

		if (this.context.shard) {
			tags.push(`shard:${this.context.shard}`);
		}

		if (this.context.product) {
			tags.push(`product:${this.context.product}`);
		}

		const { FRONTEND_VERSION: frontendVersion, RELEASE_TRACK: releaseTrack } = getBuildInfo();

		if (frontendVersion) {
			tags.push(`version:${frontendVersion}`);
		}

		if (releaseTrack) {
			tags.push(`track:${releaseTrack}`);
		}

		return tags;
	}

	private onBatch(batch: MetricEvent[]): void {
		const payload: TelemetryPayload = {
			type: 'metric',
			meta: {
				globalTags: this.formatContextAsTags(),
			},
			data: batch,
		};

		this.send(payload);
	}

	private onErrorBatch(batch: ErrorEvent[]): void {
		const payload: TelemetryErrorPayload = {
			type: 'error',
			meta: {
				globalTags: this.formatContextAsTags(),
			},
			data: batch,
		};

		this.send(payload);
	}

	private send(telemetryPayload: TelemetryPayload | TelemetryErrorPayload): void {
		try {
			logger.info`Sending monitoring events: ${telemetryPayload}`;

			const reportingUrl = '/gateway/api/telemetry';
			const data = JSON.stringify(telemetryPayload);
			let isSentViaBeacon: boolean = false;
			if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
				isSentViaBeacon = navigator.sendBeacon(reportingUrl, data);
			}

			if (!isSentViaBeacon) {
				// resorting to XMLHttpRequest instead of fetch here so that we can submit these events *synchronously* when
				// the page unloads (`fetch` can't do that)
				const client = new XMLHttpRequest();
				const shouldBeSync = this.isWindowUnloading;
				client.open('POST', reportingUrl, !shouldBeSync);
				// we're setting the "Content-Type" header to "text/plain" to behave as closely to `sendBeacon` as possible
				client.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
				client.send(data);
			}
		} catch (e) {
			// ignore - there's nothing we can do when monitoring is down
			logger.error`Sending monitoring data failed: ${e}`;
		}
	}

	private createMetric({
		type,
		name,
		value,
		tags = [],
	}: Pick<MetricEvent, 'type' | 'name' | 'value'> &
		Partial<Pick<MetricEvent, 'tags'>>): MetricEvent {
		return {
			type,
			name,
			value,
			sampleRate: 1,
			tags,
			timestamp: new Date().toISOString(),
		};
	}

	private trimTextLength(text: string, trimmedLength: number): string {
		if (!text || !trimmedLength) return text;
		if (text.length > trimmedLength) {
			return `${text.substring(0, trimmedLength)}...`;
		}

		return text;
	}
}

function isErrorIgnorableByOnCall(
	error: Error,
	attribution: string,
	attrs: LogSafeErrorAttributes,
) {
	// make sure that this condition remains first, as it should override everything else
	if (attrs.failedSlaExperience) {
		// non-ignorable, because it resulted in a failure of an SLA-bound experience
		return false;
	}

	if (containsNetworkError(error)) {
		// ignorable, because on-call can't do much about user-side network connectivity
		return true;
	}

	if (attribution === 'backbone-loadable') {
		// while we do want to report those, we don't want to overwhelm our error rate detector
		return true;
	}

	if (
		error.message.includes('.PermissionException') ||
		error.message.includes('Authorization failed: Principal has insufficient permissions') ||
		error.message.includes('.NotFoundException') ||
		attrs.statusCode === 401 ||
		attrs.statusCode === 403
	) {
		// on-call engineers can't do much about those errors, because they indicate a client-side issue
		return true;
	}

	return false;
}
